import { useMemo } from 'react';

import { PaginationInfo, UserIdentityAppModel } from '@api';

import useFilterSearchParams from '@common/hooks/use-filter-search-params';

import { useAppListIdentities } from '@common/services/accountQueries';
import { AttributeFilterOption } from '@Identities/organisms/UsersListFilters/AttributeFilter';
import { ATTRIBUTE_FILTER_PARAM, ROLE_FILTER_PARAM } from '@Identities/common/constants';
import { UsersFiltersAttributeFilter } from '@Identities/organisms/UsersFilters';
import { RolesFilterOption } from '@Identities/organisms/UsersListFilters/RolesFilter';
import { useSearchParams } from 'react-router-dom';

const LIMIT = 2500;
const PER_PAGE_LIMIT = 50;
const PAGE_PARAM = 'page';

export default function useIdentitiesFetch() {
  const [queryParams, setQueryParams] = useSearchParams();
  const { identities, isFetched } = useAppListIdentities({
    limit: LIMIT,
    skip: 0,
  });
  const { isFilteredBy, isSearchedBy } = useFilterSearchParams();

  return useMemo(() => {
    const identitiesList: UserIdentityAppModel[] = [];

    const handleOnPageChange = (pageNumber: number) => {
      queryParams.set(PAGE_PARAM, String(pageNumber));
      setQueryParams(queryParams);
      window.scrollTo(0, 0);
    };

    const attributeNamesMap: Record<string, Omit<UsersFiltersAttributeFilter, 'options'>> = {};
    const attributeNameOptionsMap: Record<string, Set<string>> = {};
    const attributeFilterOptionsMap: Record<string, AttributeFilterOption> = {};

    const rolesFilterOptionsMap: Record<string, RolesFilterOption> = {};

    for (const identity of identities) {
      const attributes: string[] = [];
      const attributesToFilterBy = new Set<string>();
      const rolesToFilterBy = new Set<string>();

      for (const attribute of identity.attributes) {
        attributes.push(attribute.value);
        attributesToFilterBy.add(attribute.id);

        attributeFilterOptionsMap[attribute.id] = {
          value: attribute.id,
          label: attribute.value,
        };

        const nameMapKey = `${attribute.integration?.id}.${attribute.name}`;
        attributeNamesMap[nameMapKey] = {
          filterParam: nameMapKey,
          trigger: {
            label: attribute.name,
            icon: attribute.integration?.icons.svg ?? '/static/logo-only.svg',
          },
        };

        const nameOptionsMap = attributeNameOptionsMap[nameMapKey] || new Set();
        nameOptionsMap.add(attribute.id);
        attributeNameOptionsMap[nameMapKey] = nameOptionsMap;
      }

      for (const role of identity.roles) {
        rolesFilterOptionsMap[role] = {
          value: role,
          label: role,
        };
        rolesToFilterBy.add(role);
      }

      if (
        isFilteredBy(ATTRIBUTE_FILTER_PARAM, attributesToFilterBy) &&
        isFilteredBy(ROLE_FILTER_PARAM, rolesToFilterBy) &&
        isSearchedBy(
          new Set([identity.first_name, identity.last_name, identity.email, ...attributes, ...identity.roles]),
        )
      ) {
        identitiesList.push(identity);
      }
    }

    const attributes: UsersFiltersAttributeFilter[] = [];
    for (const [key, value] of Object.entries(attributeNamesMap)) {
      const options = Array.from(attributeNameOptionsMap[key]).map((option) => attributeFilterOptionsMap[option]);
      attributes.push({
        ...value,
        options,
      });
    }

    const pagination: PaginationInfo = {
      total: identities.length,
      limit: PER_PAGE_LIMIT,
      offset: (Number(queryParams.get(PAGE_PARAM) ?? 1) - 1) * PER_PAGE_LIMIT,
    };
    const paginatedIdentities = identitiesList.slice(pagination.offset, pagination.offset + pagination.limit);

    return {
      identitiesList: paginatedIdentities,
      filterOptions: {
        attributes,
        roles: Object.values(rolesFilterOptionsMap),
      },
      isFetched,
      total: pagination.total,
      pagination,
      handleOnPageChange,
    };
  }, [identities, isFetched, isFilteredBy, isSearchedBy, queryParams, setQueryParams]);
}
